import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { TrendingUp, PiggyBank, Building2, Calculator, ArrowRight, DollarSign, Wrench, Home, Phone, Calendar, Lock, Shield } from 'lucide-react';

const IDX_IFRAME = "https://sef.mlsmatrix.com/Matrix/public/IDX.aspx?idx=dbf21e14";

const investmentTypes = [
  { icon: Building2, title: 'Multi-Family', description: 'Duplexes, triplexes, and apartment buildings for steady rental income.', color: '#3B82F6' },
  { icon: TrendingUp, title: 'Fix & Flip', description: 'Undervalued properties with potential for renovation and quick resale.', color: '#22C55E' },
  { icon: PiggyBank, title: 'Long-Term Rentals', description: 'Single-family homes and condos for passive rental income.', color: '#D4AF37' },
  { icon: Calculator, title: 'Commercial', description: 'Retail spaces, office buildings, and mixed-use developments.', color: '#EC4899' }
];

const whyInvest = [
  'Growing population and job market',
  'No state income tax in Florida',
  'Strong tourism industry',
  'Appreciation potential',
  'Diverse property options',
  'Expert local market knowledge'
];

export const Investors = () => {
  return (
    <div data-testid="investors-page">
      {/* Off-Market Deals Funnel - TOP */}
      <section className="py-12 bg-[#0a0a0a]" data-testid="investor-funnel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="lg:pt-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
                <Lock className="w-3.5 h-3.5" /> Exclusive Access
              </div>
              <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Get <span className="text-gold-gradient">Off-Market Deals</span> Before Anyone Else
              </h1>
              <p className="text-lg text-[#808080] mb-6">
                Our investor network gets first access to off-market properties, pocket listings, and pre-foreclosures in South Florida. Build your portfolio with deals you won't find on the MLS.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <a href="tel:7863560878">
                  <Button className="btn-gold" data-testid="investor-call-btn">
                    <Phone className="w-4 h-4 mr-2" /> Call (786) 356-0878
                  </Button>
                </a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold" data-testid="investor-book-btn">
                    <Calendar className="w-4 h-4 mr-2" /> Book a Call
                  </Button>
                </a>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#111111] border border-[#222] rounded-lg p-3 text-center">
                  <p className="text-[#D4AF37] text-xl font-bold">DSCR</p>
                  <p className="text-[#808080] text-[10px]">No Income Verification</p>
                </div>
                <div className="bg-[#111111] border border-[#222] rounded-lg p-3 text-center">
                  <p className="text-[#D4AF37] text-xl font-bold">LLC</p>
                  <p className="text-[#808080] text-[10px]">Close in Entity Name</p>
                </div>
              </div>
            </div>
            <LeadForm 
              source="investor"
              title="Join the Investor List"
              subtitle="Get exclusive off-market deals, pocket listings, and investment opportunities sent directly to you."
              buttonText="Get Off-Market Deals"
              showPropertyInterest={true}
            />
          </div>
        </div>
      </section>

      {/* Investment Types */}
      <section className="py-20 bg-[#111111]" data-testid="investment-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Opportunities</p>
            <h2 className="section-title mt-2">Investment Property Types</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {investmentTypes.map((type, index) => (
              <div key={index} className="bg-[#0a0a0a] border border-[#222] p-6 rounded-xl hover:border-[#D4AF37]/30 transition-colors">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${type.color}15` }}>
                  <type.icon className="w-6 h-6" style={{ color: type.color }} />
                </div>
                <h3 className="text-lg font-medium text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>{type.title}</h3>
                <p className="text-[#808080] text-sm">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDX Property Search */}
      <section className="py-16 bg-[#0a0a0a]" data-testid="investor-idx-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="section-title">Search Investment Properties</h2>
            <p className="text-[#808080] mt-2">Browse MLS listings — for off-market deals, <a href="tel:7863560878" className="text-[#D4AF37] hover:underline">call us directly</a></p>
          </div>
          <div className="idx-container" data-testid="investor-idx-container">
            <iframe src={IDX_IFRAME} title="Investment Property Search" className="w-full min-h-[600px]" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Why Invest + CTA */}
      <section className="py-20 bg-[#111111]" data-testid="why-invest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Why South Florida</p>
              <h2 className="section-title mt-2 mb-6">The Smart Investment Choice</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {whyInvest.map((reason, index) => (
                  <div key={index} className="flex items-center text-[#F5F5F0]">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mr-3 flex-shrink-0" />
                    <span className="text-sm">{reason}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="tel:7863560878">
                  <Button className="btn-gold" data-testid="invest-cta-call">
                    <Phone className="w-4 h-4 mr-2" /> Talk to an Investor Specialist
                  </Button>
                </a>
                <Link to="/financing">
                  <Button variant="outline" className="btn-outline-gold" data-testid="invest-preapproval-btn">
                    <Shield className="w-4 h-4 mr-2" /> Get Pre-Approved (DSCR)
                  </Button>
                </Link>
              </div>
            </div>
            {/* Investor Services */}
            <div className="space-y-4">
              <Link to="/financing" className="block bg-[#0a0a0a] border border-[#D4AF37]/20 p-6 rounded-xl hover:border-[#D4AF37]/40 transition-colors group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><DollarSign className="w-6 h-6 text-[#D4AF37]" /></div>
                  <div className="flex-1">
                    <h4 className="text-[#D4AF37] font-bold">SMF — Investor Financing</h4>
                    <p className="text-[#808080] text-xs">DSCR loans, no income verification, close in LLC</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <Link to="/property-management" className="block bg-[#0a0a0a] border border-[#D4AF37]/20 p-6 rounded-xl hover:border-[#D4AF37]/40 transition-colors group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-[#D4AF37]" /></div>
                  <div className="flex-1">
                    <h4 className="text-[#D4AF37] font-bold">SRIM — Property Management</h4>
                    <p className="text-[#808080] text-xs">Full-service rental management, tenant screening, maintenance</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <Link to="/contact" className="block bg-[#0a0a0a] border border-[#D4AF37]/20 p-6 rounded-xl hover:border-[#D4AF37]/40 transition-colors group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Wrench className="w-6 h-6 text-[#D4AF37]" /></div>
                  <div className="flex-1">
                    <h4 className="text-[#D4AF37] font-bold">MMF — Make My Fix</h4>
                    <p className="text-[#808080] text-xs">Repairs, renovations, and maintenance for your investments</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
